import { useValidate } from '@flywire/react-hooks';
import { trackLRSSigned } from 'actions';
import { Nothing } from 'components/Nothing/Nothing';
import { Button } from 'components/ui/Button/Button';
import { FileInput } from 'components/ui/FileInput/FileInput';
import { Heading } from 'components/ui/Heading/Heading';
import { Tabs } from 'components/ui/Tabs/Tabs';
import { useLRSContext } from 'contexts/LRS';
import { X } from 'icons';
import { useEffect, useReducer, useRef } from 'react';
import { useDispatch } from 'react-redux';
import SignatureCanvas from 'react-signature-canvas';
import { Back } from '../../components/Back/Back';

import './Sign.scss';

const TYPES = {
  CANVAS_MOUNTED: 'CANVAS_MOUNTED',
  CLEAR: 'CLEAR',
  SET_IMAGE: 'SET_IMAGE',
  UPLOADING: 'UPLOADING',
  UPLOAD_FAILED: 'UPLOAD_FAILED',
};

const METHODS = {
  FILE: 'FILE',
  CANVAS: 'CANVAS',
};

const initialState = {
  isCanvasMounted: false,
  image: null,
  method: METHODS.CANVAS,
  uploading: false,
};

function reducer(state, action) {
  switch (action.type) {
    case TYPES.SET_IMAGE:
      return { ...state, ...action.payload, uploading: false };
    case TYPES.CLEAR:
      return initialState;
    case TYPES.UPLOADING:
      return { ...state, uploading: true };
    case TYPES.UPLOAD_FAILED:
      return { ...state, uploading: false };
    case TYPES.CANVAS_MOUNTED:
      if (state.isCanvasMounted) {
        return state;
      }

      return { ...state, isCanvasMounted: true };
    default:
      return state;
  }
}

export const Sign = () => {
  const { Tab, TabList, TabPanel, TabPanels } = Tabs;
  const constraints = { image: { presence: true } };
  const {
    i18n,
    values,
    onBack,
    updateAll,
    complete: completeStep,
    navigation,
  } = useLRSContext();

  const [state, dispatch] = useReducer(reducer, initialState, () => ({
    image: values.signatureImage,
    method: values.signatureMethod || METHODS.CANVAS,
    isCanvasMounted: !values.signatureMethod && !values.signatureImage,
  }));

  const { isValid } = useValidate(state, constraints);
  const canvasRef = useRef(null);
  const dispatchRdx = useDispatch();

  useEffect(() => {
    if (values.signatureMethod === METHODS.CANVAS && values.signatureImage) {
      canvasRef.current?.fromDataURL(values.signatureImage);
      dispatch({ type: 'CANVAS_MOUNTED' });
    }
  }, [values.signatureImage, values.signatureMethod]);

  function handleBack() {
    onBack();
  }

  function handleClear() {
    canvasRef.current?.clear();
    dispatch({ type: TYPES.CLEAR });
  }

  function handleEnd() {
    dispatch({
      type: TYPES.SET_IMAGE,
      payload: {
        image: canvasRef.current?.toDataURL(),
        method: METHODS.CANVAS,
      },
    });
  }

  function handleFileUpload(evt) {
    dispatch({ type: TYPES.UPLOADING });
    const file = evt.target.files?.[0];

    if (!file) {
      dispatch({ type: TYPES.UPLOAD_FAILED });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = function () {
      const result = reader.result;
      canvasRef.current?.clear();
      evt.target.value = '';

      dispatch({
        type: TYPES.SET_IMAGE,
        payload: {
          image: result,
          method: METHODS.FILE,
        },
      });
    };
    reader.readAsDataURL(file);
  }

  function handleSave() {
    dispatchRdx(trackLRSSigned({ method: state.method }));

    updateAll({
      signatureImage: state.image,
      signatureMethod: state.method,
    });

    completeStep();
    navigation.go('summary');
  }

  return (
    <div className="Sign flex flex-col min-h-full">
      <div className="flex-1">
        <Back onClick={handleBack} className="marginBottom-md" />
        <Heading as="h2" size="large" className="fontWeight-semiBold">
          {i18n.t('checkout.LRS.sign.title')}
        </Heading>
        <Tabs defaultActiveIndex={state.method === METHODS.FILE ? 1 : 0}>
          <TabList>
            <Tab onClick={() => dispatch({ type: 'CANVAS_MOUNTED' })}>
              {i18n.t('checkout.LRS.sign.draw')}
            </Tab>
            <Tab>{i18n.t('checkout.LRS.sign.upload')}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              {({ isActive }) => {
                if (isActive || state.isCanvasMounted) {
                  return (
                    <>
                      <p>{i18n.t('checkout.LRS.sign.draw.des')}</p>
                      <div className="Sign-canvas">
                        <SignatureCanvas ref={canvasRef} onEnd={handleEnd} />
                        <button onClick={handleClear}>
                          {i18n.t('checkout.LRS.sign.clear')}
                        </button>
                      </div>
                    </>
                  );
                }

                return <Nothing />;
              }}
            </TabPanel>
            <TabPanel>
              <div>
                <p>{i18n.t('checkout.LRS.sign.upload.des')}</p>
                <div className="ImagePreview">
                  {state.image && state.method === METHODS.FILE && (
                    <>
                      <img src={state.image} alt="" />
                      <button
                        onClick={handleClear}
                        aria-label={i18n.t('checkout.LRS.sign.upload.delete')}
                        className="cursor-pointer m-2"
                      >
                        <X aria-hidden />
                      </button>
                    </>
                  )}
                </div>
                <FileInput
                  accept=".png, .jpg"
                  buttonText={i18n.t('checkout.LRS.sign.upload') as string}
                  className="Sign-upload mt-6"
                  onChange={handleFileUpload}
                  uploading={state.uploading}
                />
              </div>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
      <div className="mt-auto pt-6 pb-4">
        <Button
          tone="primary"
          size="full"
          disabled={!isValid}
          onClick={handleSave}
          data-testid="Save"
        >
          {i18n.t('checkout.LRS.steps.save.button')}
        </Button>
      </div>
    </div>
  );
};
